import firebase from 'firebase/compat/app';
import 'firebase/compat/remote-config';
import { TIMEOUT_DEFAULTS } from './helpers/timeout.constants';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || ''
};

if (!firebase.apps.length) {
  try {
    firebase.initializeApp(firebaseConfig);
  } catch (error) {
    console.error('Error initializing Firebase:', error);
  }
}

let remoteConfig: firebase.remoteConfig.RemoteConfig | null = null;

const defaults: Record<string, boolean | number | string> = {
  google_ads_enabled: false,
  google_adtest: false,
  ...TIMEOUT_DEFAULTS,
};

// Singleton: concurrent callers share one in-flight init rather than each issuing fetchAndActivate.
let initPromise: Promise<firebase.remoteConfig.RemoteConfig | null> | null = null;

export const initRemoteConfig = async (): Promise<firebase.remoteConfig.RemoteConfig | null> => {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      remoteConfig = firebase.remoteConfig();
      remoteConfig.defaultConfig = defaults;
      remoteConfig.settings = {
        minimumFetchIntervalMillis: process.env.NODE_ENV === 'development' ? 0 : 60000,
        fetchTimeoutMillis: 60000
      };
      await remoteConfig.fetchAndActivate();
      console.log('Firebase Remote Config initialized successfully');
      return remoteConfig;
    } catch (error) {
      console.error('Error initializing Firebase Remote Config:', error);
      initPromise = null;
      return null;
    }
  })();
  return initPromise;
};

const getRemoteConfigValue = (key: string, asBoolean = false, asString = false): boolean | number | string => {
  if (!remoteConfig) {
    return defaults[key];
  }

  if (asBoolean) {
    return remoteConfig.getValue(key).asBoolean();
  }

  if (asString) {
    return remoteConfig.getValue(key).asString();
  }

  return defaults[key];
};

// Keys already warned about, so an out-of-range value logs once rather than on every request.
const outOfRangeKeys = new Set<string>();

// A value below `min` (or non-positive) is a misconfiguration: use the fallback instead.
export const getRemoteConfigNumber = (key: string, fallback: number, min = 0): number => {
  try {
    const value = remoteConfig ? remoteConfig.getValue(key).asNumber() : defaults[key];
    if (typeof value === 'number' && Number.isFinite(value) && value > 0 && value >= min) return value;
    if (!outOfRangeKeys.has(key)) {
      outOfRangeKeys.add(key);
      console.warn(`Remote Config '${key}' is ${value}, below the ${min}ms floor; using ${fallback}ms`);
    }
    return fallback;
  } catch (error) {
    console.error(`Error getting Remote Config number value for key '${key}':`, error);
    return fallback;
  }
};

export const getRemoteConfigString = (key: string): string => {
  try {
    return getRemoteConfigValue(key, false, true) as string;
  } catch (error) {
    console.error(`Error getting Remote Config value for key '${key}':`, error);
    return (defaults[key] as string) || '';
  }
};

export const getRemoteConfigBoolean = (key: string): boolean => {
  try {
    return getRemoteConfigValue(key, true) as boolean;
  } catch (error) {
    console.error(`Error getting Remote Config boolean value for key '${key}':`, error);
    return defaults[key] as boolean;
  }
};
